import { type TSearchConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';

/**
 * Internal regex used to extract a cloudId from a Resource ARI of the form:
 *   ari:cloud:<product>::<resourceType>/<cloudId>
 * Capture group 1 yields the cloudId.
 *
 * Public via `getFirstCloudId(...)` and is not exported directly.
 */
const EXTRACT_CLOUD_ID = /ari:cloud:.*?\/(.*)/;

/**
 * Regex used to extract a product key from a Resource ARI of the form:
 *   ari:cloud:<product>::...
 * Capture group 1 yields the product key.
 *
 * Exported so the `useRovoConfig` hook (and any future callers that need to
 * parse resource ARIs in the same way) can reuse the same pattern instead
 * of redeclaring it locally.
 */
export const EXTRACT_PRODUCT_FROM_ARI: RegExp = /ari:cloud:(.*)::/;

/**
 * Returns the cloudId encoded in the first resource ARI of a search-config
 * response. Returns an empty string when the response has no resources or
 * the ARI doesn't match the expected shape.
 */
export const getFirstCloudId = (firstPartyResponse: TSearchConfigAPIResponse): string => {
	const match = firstPartyResponse.resources[0]?.id.match(EXTRACT_CLOUD_ID);
	const extractedCloudId = match ? match[1] : '';

	return extractedCloudId;
};

/**
 * Returns whether the given site has Rovo enabled, by looking up the
 * `siteMetadata` entry that matches `cloudId`.
 *
 * Returns `false` when `cloudId` or the response is missing. Returns
 * `undefined` when no matching siteMetadata entry exists (or when the
 * matching entry's `isRovoEnabled` field is absent), since we can't make
 * a positive negative claim from the absence of data.
 */
export const isRovoEnabledForSite = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): boolean | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isRovoEnabled;
};

/**
 * Returns whether the given site has its Jira issue index ready for use, by
 * looking up the `siteMetadata` entry that matches `cloudId`.
 *
 * Returns `false` when `cloudId` or the response is missing. Returns
 * `undefined` when no matching siteMetadata entry exists (or when the
 * matching entry's `isJiraIssueIndexed` field is absent).
 */
export const getJiraIssueIndexedForTenant = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): boolean | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isJiraIssueIndexed;
};

/**
 * Returns the `productEdition` for the given site, or undefined when the
 * site has no matching metadata entry with a `productEdition`.
 */
export const getProductEdition = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['productEdition'] | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productEdition,
	)?.productEdition;
};

/**
 * Returns the `productDataRegions` for the given site, or undefined when
 * the site has no matching metadata entry with `productDataRegions`.
 */
export const getProductDataRegions = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['productDataRegions'] | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productDataRegions,
	)?.productDataRegions;
};

/**
 * Returns the `rovoFeatures` object for the given site, or undefined when
 * the site has no matching metadata entry with `rovoFeatures`.
 */
export const getRovoFeatures = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['rovoFeatures'] | undefined => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.rovoFeatures,
	)?.rovoFeatures;
};
